// src/App.jsx
import React, { Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { FixedToastContainer } from "./components/FixedToastContainer";
import ErrorBoundary from "./components/ErrorBoundary";
import Navbar from "./components/Navbar";
import ProtectedRoute from "./components/ProtectedRoute";
import Loading from "./components/Loading";
import CSSDebug from "./components/CSSDebug";
import Contact from "./components/Contact";
import "react-toastify/dist/ReactToastify.css";

import * as Pages from "./pages";

// Lazy helper
const lazyPage = (page) => React.lazy(() => Promise.resolve({ default: page }));

/* =======================
   PUBLIC PAGES
======================= */
const Home = lazyPage(Pages.Home);
const Register = lazyPage(Pages.Register);
const Login = lazyPage(Pages.Login);
const ForgotPassword = lazyPage(Pages.ForgotPassword);
const ResetPassword = lazyPage(Pages.ResetPassword);
const ConfirmAccount = lazyPage(Pages.ConfirmAccount);
const Courses = lazyPage(Pages.Courses);
const CourseViewer = lazyPage(Pages.CourseViewer);
const PreviewPage = lazyPage(Pages.PreviewPage);
const CoursePreviewPage = lazyPage(Pages.CoursePreviewPage);
const PaymentSuccess = lazyPage(Pages.PaymentSuccess);
const PaymentCancel = lazyPage(Pages.PaymentCancel);
const Cancel = lazyPage(Pages.Cancel);
const NotFound = lazyPage(Pages.NotFound);
const Unauthorized = lazyPage(Pages.Unauthorized);

/* =======================
   PROTECTED PAGES
======================= */
const AdminDashboard = lazyPage(Pages.AdminDashboard);
const ManageCourses = lazyPage(Pages.ManageCourses);
const ManageUsers = lazyPage(Pages.ManageUsers);
const TeacherManageLessons = lazyPage(Pages.TeacherManageLessons);
const CreateCourse = lazyPage(Pages.CreateCourse);
const CreateCourseWithUnits = lazyPage(Pages.CreateCourseWithUnits);
const CourseLessons = lazyPage(Pages.CourseLessons);
const EditCourse = lazyPage(Pages.EditCourse);
const CreateLessonPage = lazyPage(Pages.CreateLessonPage);
const MyTeachingCourses = lazyPage(Pages.MyTeachingCourses);
const TeacherCourseViewer = lazyPage(Pages.TeacherCourseViewer);
const StudentLessons = lazyPage(Pages.StudentLessons);
const MyCoursesPage = lazyPage(Pages.MyCoursesPage);
const Profile = lazyPage(Pages.Profile);
const FileManager = lazyPage(Pages.FileManager);
const PaymentPage = lazyPage(Pages.PaymentPage);

// Dynamic imports
const EditLesson = React.lazy(() => import("./pages/teachers/EditLesson"));
const AdminLayout = React.lazy(() => import("./components/AdminLayout"));
const PendingStudents = React.lazy(() => import("./components/PendingStudents"));
const PendingEnrollments = React.lazy(() => import("./components/PendingEnrollments"));
const AdminLessonLogs = React.lazy(() => import("./pages/AdminLessonLogs"));

/* =======================
   PUBLIC ROUTE
======================= */
const PublicRoute = ({ children }) => {
  const { isAuthenticated, user, checked } = useAuth();

  if (!checked) return null;

  if (isAuthenticated && user?.role) {
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    if (user.role === "teacher") return <Navigate to="/teacher-dashboard" replace />;
    if (user.role === "student") return <Navigate to="/my-courses" replace />;
  }

  return children;
};

/* =======================
   ROLE REDIRECT
======================= */
const RoleBasedRedirect = () => {
  const { user } = useAuth();

  if (!user) return <Navigate to="/login" replace />;

  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "teacher") return <Navigate to="/teacher-dashboard" replace />;
  if (user.role === "student") return <Navigate to="/my-courses" replace />;

  return <Navigate to="/" replace />;
};

/* =======================
   APP CONTENT
======================= */
function AppContent() {
  return (
    <div className="app">
      <CSSDebug />

      <ErrorBoundary>
        <Navbar />

        <main className="main-content">
          <Suspense fallback={<Loading />}>
            <Routes>
              {/* PUBLIC */}
              <Route path="/" element={<Home />} />
              <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/confirm-account" element={<ConfirmAccount />} />
              <Route path="/courses" element={<Courses />} />
              <Route path="/courses/:slug" element={<CourseViewer />} />
              <Route path="/preview/:lessonId" element={<PreviewPage />} />
              <Route path="/courses/:courseId/preview" element={<CoursePreviewPage />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/payment-cancel" element={<PaymentCancel />} />
              <Route path="/cancel" element={<Cancel />} />
              <Route path="/dashboard" element={<RoleBasedRedirect />} />

              {/* ADMIN */}
              <Route path="/admin/*" element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminLayout />
                </ProtectedRoute>
              }>
                <Route index element={<AdminDashboard />} />
                <Route path="pending-students" element={<PendingStudents />} />
                <Route path="pending-enrollments" element={<PendingEnrollments />} />
                <Route path="manage-courses" element={<ManageCourses />} />
                <Route path="manage-users" element={<ManageUsers />} />
                <Route path="lesson-logs" element={<AdminLessonLogs />} />
                <Route path="file-manager" element={<FileManager />} />
              </Route>

              {/* TEACHER */}
              <Route path="/teacher-dashboard" element={
                <ProtectedRoute allowedRoles={["teacher"]}>
                  <MyTeachingCourses />
                </ProtectedRoute>
              } />

              <Route path="/teacher/courses/:courseId/view" element={
                <ProtectedRoute allowedRoles={["teacher", "admin"]}>
                  <TeacherCourseViewer />
                </ProtectedRoute>
              } />

              <Route path="/teacher/lessons/:lessonId/edit" element={
                <ProtectedRoute allowedRoles={["teacher", "admin"]}>
                  <EditLesson />
                </ProtectedRoute>
              } />

              {/* STUDENT */}
              <Route path="/my-courses" element={
                <ProtectedRoute allowedRoles={["student", "teacher", "admin"]}>
                  <MyCoursesPage />
                </ProtectedRoute>
              } />

              <Route path="/profile" element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } />

              <Route path="/payment/:courseId" element={
                <ProtectedRoute allowedRoles={["student"]}>
                  <PaymentPage />
                </ProtectedRoute>
              } />

              {/* ERRORS */}
              <Route path="/unauthorized" element={<Unauthorized />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>

        <FixedToastContainer />

        {/* ✅ FOOTER RESTORED */}
        <footer className="app-footer">
          <p>© {new Date().getFullYear()} Math Class Platform. All rights reserved.</p>
          <p className="footer-help">
            Need help? <a href="/contact">Contact Support</a>
          </p>
        </footer>

      </ErrorBoundary>
    </div>
  );
}

/* =======================
   APP ROOT
======================= */
function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
